import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CartItem {
  id: string;
  name: string;
  image: string;
  price: number;
  quantity: number;
  totalPrice: number;
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private cartItems: CartItem[] = [];
  private cartSubject = new BehaviorSubject<CartItem[]>(this.cartItems);

  constructor() {}

  // Obtener el carrito como Observable
  getCart(): Observable<CartItem[]> {
    return this.cartSubject.asObservable();
  }

  // Obtener el carrito como array
  getCartItems(): CartItem[] {
    return [...this.cartItems];
  }

  // Agregar producto al carrito
  addToCart(product: Partial<CartItem>): void {
    const existingItem = this.cartItems.find(item => item.id === product.id);

    if (existingItem) {
      existingItem.quantity += product.quantity || 1;
      existingItem.totalPrice = existingItem.price * existingItem.quantity;
    } else {
      const newItem: CartItem = {
        id: product.id || '',
        name: product.name || '',
        image: product.image || '',
        price: product.price || 0,
        quantity: product.quantity || 1,
        totalPrice: (product.price || 0) * (product.quantity || 1)
      };
      this.cartItems.push(newItem);
    }

    this.cartSubject.next([...this.cartItems]);
  }

  replaceLastItem(product: Partial<CartItem>): void {
    if (this.cartItems.length > 0) {
      // Crear un nuevo item con los valores por defecto
      const newItem: CartItem = {
        id: product.id || '',
        name: product.name || '',
        image: product.image || '',
        price: product.price || 0,
        quantity: product.quantity || 1,
        totalPrice: (product.price || 0) * (product.quantity || 1)
      };

      // Reemplazar el último elemento
      this.cartItems[this.cartItems.length - 1] = newItem;
      this.cartSubject.next([...this.cartItems]);
    }
  }

  // Actualizar cantidad de un producto
  updateQuantity(productId: string, quantity: number): void {
    const item = this.cartItems.find(item => item.id === productId);
    if (item) {
      if (quantity <= 0) {
        this.removeFromCart(productId);
      } else {
        item.quantity = quantity;
        item.totalPrice = item.price * quantity;
        this.cartSubject.next([...this.cartItems]);
      }
    }
  }

  // Incrementar cantidad
  incrementQuantity(productId: string): void {
    const item = this.cartItems.find(item => item.id === productId);
    if (item) {
      item.quantity++;
      item.totalPrice = item.price * item.quantity;
      this.cartSubject.next([...this.cartItems]);
    }
  }

  // Decrementar cantidad
  decrementQuantity(productId: string): void {
    const item = this.cartItems.find(item => item.id === productId);
    if (item) {
      if (item.quantity > 1) {
        item.quantity--;
        item.totalPrice = item.price * item.quantity;
        this.cartSubject.next([...this.cartItems]);
      } else {
        // Si la cantidad es 1, al decrementar llega a 0 y se elimina automáticamente
        this.removeFromCart(productId);
      }
    }
  }

  // Remover producto del carrito
  removeFromCart(productId: string): void {
    this.cartItems = this.cartItems.filter(item => item.id !== productId);
    this.cartSubject.next([...this.cartItems]);
  }

  // Obtener cantidad total de items
  getTotalItems(): number {
    return this.cartItems.reduce((total, item) => total + item.quantity, 0);
  }

  // Obtener precio total del carrito
  getTotalPrice(): number {
    return this.cartItems.reduce((total, item) => total + item.totalPrice, 0);
  }

  // Limpiar carrito
  clearCart(): void {
    this.cartItems = [];
    this.cartSubject.next([]);
  }

  // Verificar si el carrito está vacío
  isEmpty(): boolean {
    return this.cartItems.length === 0;
  }
}
